import { renderPrintDocument } from "../../core/renderPrintDocument";
import { escapeHtml } from "../../shared/escapeHtml";
import { createNorPrintViewModelV2 } from "./createNorPrintViewModelV2";

const norPrintStylesV2 = `
  @page {
    size: A4 portrait;
    margin: 20mm;
  }

  html, body {
    background: #ffffff;
    color: #111111;
    font-family: Georgia, "Times New Roman", Times, serif;
  }

  body {
    padding: 0;
  }

  .nor-document {
    width: 100%;
    max-width: 170mm;
    margin: 0 auto;
    color: #111111;
  }

  .nor-header {
    width: 100%;
    max-width: 136mm;
    margin: 0 auto 5mm;
    display: grid;
    grid-template-columns: 40mm 1fr;
    gap: 8mm;
    align-items: start;
  }

  .nor-logo-wrap {
    min-height: 20mm;
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
  }

  .nor-logo {
    max-width: 42mm;
    max-height: 20mm;
    width: auto;
    height: auto;
    object-fit: contain;
  }

  .nor-header-main {
    text-align: left;
  }

  .nor-vessel-name {
    margin: 0 0 2mm;
    font-size: 18pt;
    font-weight: 700;
    line-height: 1.15;
    letter-spacing: 0.01em;
  }

  .nor-title {
    margin: 0;
    font-size: 14pt;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
  }

  .nor-rule {
    border: 0;
    border-top: 0.35mm solid #111111;
    margin: 0 0 5mm;
  }

  .nor-meta {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    margin-bottom: 4mm;
  }

  .nor-meta td {
    padding: 1.1mm 3.5mm 1.1mm 0;
    vertical-align: top;
    font-size: 10.5pt;
  }

  .nor-meta-label {
    width: 15%;
    font-weight: 600;
    white-space: nowrap;
  }

  .nor-meta-value {
    width: 35%;
    white-space: nowrap;
  }

  .nor-meta-separator {
    border: 0;
    border-top: 0.2mm solid #c9c9c9;
    margin: 0 0 5.5mm;
  }

  .nor-declaration {
    margin-bottom: 12mm;
    page-break-inside: avoid;
  }

  .nor-paragraph {
    margin: 0 0 2.8mm;
    font-size: 11pt;
    line-height: 1.5;
  }

  .nor-inline-strong {
    font-weight: 600;
    white-space: nowrap;
  }

  .nor-readiness-value {
    font-weight: 700;
    white-space: nowrap;
  }

  .nor-cargo-block {
    margin: 3.8mm 0 4.2mm;
    padding-left: 0;
  }

  .nor-cargo-name {
    margin: 0 0 1mm;
    font-size: 13pt;
    font-weight: 700;
    line-height: 1.25;
  }

  .nor-cargo-quantity {
    margin: 0;
    font-size: 11.5pt;
    font-weight: 700;
    white-space: nowrap;
  }

  .nor-receipt {
    margin: 14mm 0 18mm;
    page-break-inside: avoid;
  }

  .nor-receipt-grid {
    display: grid;
    grid-template-columns: 18mm 1fr;
    column-gap: 4mm;
    align-items: end;
    width: 100%;
    margin-bottom: 8mm;
  }

  .nor-receipt-row-secondary {
    display: grid;
    grid-template-columns: 18mm minmax(0, 1fr) 14mm minmax(0, 1fr);
    column-gap: 5mm;
    align-items: end;
    font-size: 10.5pt;
    width: 100%;
  }

  .nor-receipt-label {
    font-weight: 700;
    white-space: nowrap;
  }

  .nor-line {
    border-bottom: 0.25mm solid #111111;
    display: block;
    min-height: 12mm;
    width: 100%;
  }

  .nor-line-short {
    border-bottom: 0.25mm solid #111111;
    display: block;
    min-height: 12mm;
    width: 100%;
  }

  .nor-signatures {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 18mm;
    margin-top: 34mm;
    page-break-inside: avoid;
    align-items: start;
  }

  .nor-signature-block {
    display: grid;
    grid-template-rows: auto 14mm;
    align-items: start;
    width: 100%;
  }

  .nor-signature-line {
    border-top: 0.25mm solid #111111;
    padding-top: 2mm;
    font-size: 10pt;
    white-space: nowrap;
    width: 100%;
  }

  .nor-signature-body {
    width: 100%;
  }

  .nor-signature-caption {
    min-height: 14mm;
    padding-top: 2mm;
  }

  .nor-master-name {
    margin-top: 3mm;
    font-size: 10.5pt;
    font-weight: 700;
    white-space: nowrap;
  }

  .nowrap {
    white-space: nowrap;
  }

  @media print {
    .nor-document,
    .nor-header,
    .nor-declaration,
    .nor-receipt,
      .nor-signatures {
      break-inside: avoid;
    }
  }
`;

const renderNorBody = (model, t) => `
  <main class="nor-document">
    <header class="nor-header">
      <div class="nor-logo-wrap">
        ${
          model.companyLogo
            ? `<img src="${escapeHtml(model.companyLogo)}" alt="Company logo" class="nor-logo" />`
            : ""
        }
      </div>
      <div class="nor-header-main">
        <h1 class="nor-vessel-name">${escapeHtml(model.vesselName)}</h1>
        <h2 class="nor-title">${escapeHtml(t("forprint.noticeOfReadiness"))}</h2>
      </div>
    </header>

    <hr class="nor-rule" />

    <table class="nor-meta" aria-label="Document metadata">
      <tbody>
        <tr>
          <td class="nor-meta-label">${escapeHtml(t("forprint.to"))}:</td>
          <td class="nor-meta-value"><span class="nowrap">${escapeHtml(
            model.receiverName || "\u2014"
          )}</span></td>
          <td class="nor-meta-label">${escapeHtml(t("forprint.port"))}:</td>
          <td class="nor-meta-value"><span class="nowrap">${escapeHtml(
            model.portName || "\u2014"
          )}</span></td>
        </tr>
        <tr>
          <td class="nor-meta-label">Voyage No.:</td>
          <td class="nor-meta-value"><span class="nowrap">${escapeHtml(
            model.voyageNumber || "\u2014"
          )}</span></td>
          <td class="nor-meta-label">${escapeHtml(t("forprint.date"))}:</td>
          <td class="nor-meta-value"><span class="nowrap">${escapeHtml(
            model.documentDate || "\u2014"
          )}</span></td>
        </tr>
      </tbody>
    </table>

    <hr class="nor-meta-separator" />

    <section class="nor-declaration">
      <p class="nor-paragraph">
        ${escapeHtml(t("forprint.iHerebyTender"))}:
        <span class="nor-inline-strong">${escapeHtml(
          model.vesselIdentityLine || "\u2014"
        )}</span>
      </p>
      <p class="nor-paragraph">
        ${escapeHtml(t("forprint.readyTo"))}:
        <span class="nor-inline-strong">${escapeHtml(model.operationLabel)}</span>
        her cargo of:
      </p>
      <div class="nor-cargo-block">
        <p class="nor-cargo-name">${escapeHtml(model.cargoName || "\u2014")}</p>
        <p class="nor-cargo-quantity">
          <span class="nowrap">${escapeHtml(model.cargoQuantityDisplay || "")}</span>
          <span class="nowrap"> ${escapeHtml(model.cargoUnitDisplay || "")}</span>
        </p>
      </div>
      <p class="nor-paragraph">
        ${escapeHtml(t("forprint.asFromDate"))}:
        <span class="nor-readiness-value">${escapeHtml(
          model.tenderedDate || "\u2014"
        )}</span>
        ${escapeHtml(t("forprint.hrs"))}:
        <span class="nor-readiness-value">${escapeHtml(
          model.tenderedTime || "\u2014"
        )}</span>
      </p>
      ${
        model.retendered
          ? `<p class="nor-paragraph">${escapeHtml(t("forprint.retenderedText"))}</p>`
          : ""
      }
    </section>

    <section class="nor-receipt">
      <div class="nor-receipt-grid">
        <span class="nor-receipt-label">${escapeHtml(t("forprint.received"))}:</span>
        <span class="nor-line"></span>
      </div>
      <div class="nor-receipt-row-secondary">
        <span class="nor-receipt-label">${escapeHtml(t("forprint.date"))}:</span>
        <span class="nor-line-short"></span>
        <span class="nor-receipt-label">${escapeHtml(t("forprint.time"))}:</span>
        <span class="nor-line-short"></span>
      </div>
    </section>

    <section class="nor-signatures">
      <div class="nor-signature-block">
        <div class="nor-signature-body">
          <div class="nor-signature-line"></div>
          <div class="nor-signature-caption">${escapeHtml(
            t("forprint.signatureRepresentative")
          )}</div>
        </div>
      </div>
      <div class="nor-signature-block">
        <div class="nor-signature-body">
          <div class="nor-signature-line"></div>
          <div class="nor-signature-caption">
            <div>${escapeHtml(t("forprint.masterStamp"))}</div>
            <div class="nor-master-name">${escapeHtml(
              model.masterName || "\u2014"
            )}</div>
          </div>
        </div>
      </div>
    </section>
  </main>
`;

export const renderNorV2 = ({
  currentVoyage,
  selectedTripId,
  selectedCargoIndex,
  companyLogo,
  retendered = false,
  is24Hour,
  callSign,
  date,
  t,
}) => {
  const model = createNorPrintViewModelV2({
    currentVoyage,
    selectedTripId,
    selectedCargoIndex,
    companyLogo,
    retendered,
    is24Hour,
    callSign,
    date,
    t,
  });

  return renderPrintDocument({
    title: t("forprint.printNOR"),
    templateId: "nor",
    viewModel: model,
    styles: norPrintStylesV2,
    body: renderNorBody(model, t),
  });
};
